import React from 'react'
import styled from 'styled-components'
import { ChevronDown, ChevronUp, Info } from 'lucide-react'
import {
  colors,
  spacing,
  borderRadius,
  typographyMixin,
  heading,
  body,
  mediaQuery,
} from 'src/constants/marketing-ds'
import { FAQ_DATA } from '../data/faqs'
import { fireUIInteractionEvent } from 'src/modules/analytics/v3'
import { selectStateForProtobuf } from 'src/modules/reducers'
import { useSelector } from 'react-redux'

const FAQSection = ({ activeFaq, onToggleFaq, tweetText }) => {
  const stateForProtobuf = useSelector(selectStateForProtobuf)

  const handleOnClick = (idx) => {
    onToggleFaq(idx)
    fireUIInteractionEvent(
      {
        element: FAQ_DATA[idx].question,
        component: 'faq_item_clicked',
        type: 'clicked',
      },
      stateForProtobuf
    )
  }

  return (
    <Section>
      <Container>
        <Header>
          <Info size={24} />
          <Title>Frequently Asked Questions</Title>
        </Header>
        <List>
          {FAQ_DATA.map((faq, idx) => (
            <Item key={faq.id}>
              <Button onClick={() => handleOnClick(idx)}>
                <Question>{faq.question}</Question>
                {activeFaq === idx ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </Button>
              {activeFaq === idx && (
                <Answer>
                  {typeof faq.answer === 'function' ? faq.answer(tweetText) : faq.answer}
                </Answer>
              )}
            </Item>
          ))}
        </List>
      </Container>
    </Section>
  )
}

export default FAQSection

const Section = styled.section`
  padding: ${spacing.xxl} ${spacing.md};
  background-color: ${colors.white};
`

const Container = styled.div`
  max-width: 768px;
  margin: 0 auto;
`

const Header = styled.h2`
  ${typographyMixin(heading.md)}
  font-weight: 700;
  margin-bottom: ${spacing.lg};
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${colors.slate[60]};
  ${mediaQuery.tablet} {
    ${typographyMixin(heading.lg)}
  }

  svg {
    color: ${colors.slate[100]};
    font-size: 32px;
  }
`

const Title = styled.span``

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
`

const Item = styled.div`
  border: 1px solid ${colors.slate[160]};
  border-radius: ${borderRadius.md};
  background-color: ${colors.white};
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`

const Button = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing.sm};
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${colors.slate[190]};
  }

  svg {
    color: ${(props) => (props.$active ? colors.orange[90] : colors.slate[60])};
    flex-shrink: 0;
  }
`

const Question = styled.span`
  ${typographyMixin(body.md)}
  font-weight: 700;

  color: ${colors.slate[60]};
  transition: color 0.2s;

  ${Button}:hover & {
    color: ${colors.slate[60]};
  }
`

const Answer = styled.div`
  padding: ${spacing.sm};
  padding-top: 0;
  color: ${colors.slate[60]};
  line-height: 1.6;
  border-top: 1px solid ${colors.slate[160]};
  margin-top: 8px;

  a {
    color: #3b82f6;
    text-decoration: underline;
    transition: color 0.2s;

    &:hover {
      color: #2563eb;
    }
  }
`
